import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Sparkles, Loader2, CheckCircle2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { requestLoginLink } from '../lib/funnel-api';

function readLocalRedirect(value: string | null): string | null {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return null;
  return value;
}

// Issue #978: a buyer who just paid can land here as the fallback when a
// same-device claim isn't available (cross-device, or the account-creation
// webhook hasn't landed yet). `from=purchase` distinguishes that context from
// an organic returning-user visit so we can (a) prefill the checkout email,
// (b) drop the "Welcome back" framing that's wrong for a 60-second-old
// customer, and (c) remove the prod-waitlist "Get Early Access" link, which
// has no business in front of someone who already paid.
function readPostPurchaseEmail(value: string | null): string {
  if (!value) return '';
  try {
    return decodeURIComponent(value);
  } catch {
    return '';
  }
}

export function LoginPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  // Issue #978: the post-purchase fallback lands here with ?from=purchase and
  // the buyer's email, so the form is prefilled and framed as "finish signing
  // in" rather than a cold login wall.
  const fromPurchase = params.get('from') === 'purchase';
  const [email, setEmail] = useState(() => readPostPurchaseEmail(params.get('email')));
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [retryAfter, setRetryAfter] = useState(0);
  // Issue #983 item 4: /login rendered the sign-in form even with an active
  // session, with no redirect to /start. Gate the form behind a session
  // check so an already-signed-in visitor never sees it.
  const [checkingSession, setCheckingSession] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      if (data.session) {
        const redirect = readLocalRedirect(params.get('redirect'));
        nav(redirect ?? '/start', { replace: true });
        return;
      }
      setCheckingSession(false);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (retryAfter <= 0) return;
    const id = setTimeout(() => setRetryAfter((s) => Math.max(0, s - 1)), 1000);
    return () => clearTimeout(id);
  }, [retryAfter]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const redirect = readLocalRedirect(params.get('redirect'));
    const result = await requestLoginLink({ email, redirect });
    if (result.status === 'sent') {
      setSent(true);
    } else if (result.status === 'rate_limited') {
      // Issue #983 item 5: a repeat request used to produce no email and no
      // feedback. Be honest about it and show a live retry countdown
      // (rendered from `retryAfter` below, ticking down each second).
      setRetryAfter(result.retryAfterSeconds);
    } else {
      setError('Something went wrong sending the link. Please try again in a moment.');
    }
    setSubmitting(false);
  };

  if (checkingSession) return null;

  return (
    <div data-theme="dark" className="relative min-h-screen overflow-hidden bg-[#080a0f] text-white antialiased">
      {/* Brand glow — three diffuse coral circles, matching the marketing surfaces. */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute top-[-80px] left-[-60px] w-96 h-96 rounded-full bg-[#fe5963]/[0.18] blur-3xl"></div>
        <div className="absolute bottom-[-60px] left-1/3 w-72 h-72 rounded-full bg-[#fe5963]/[0.10] blur-3xl"></div>
        <div className="absolute top-1/4 right-[-80px] w-80 h-80 rounded-full bg-[#fe5963]/[0.08] blur-3xl"></div>
      </div>

      <div className="relative flex min-h-screen">
        {/* Left side: branding */}
        <div className="hidden lg:flex lg:w-1/2 relative">
          <div className="relative z-10 flex flex-col justify-center px-16 lg:pl-32">
            <img
              src="/roleforge-logo-horizontal.svg"
              alt="RoleForge"
              className="h-28 w-auto object-contain self-start mb-8 drop-shadow-[0_0_24px_rgba(254,89,99,0.35)]"
            />
            <h1 className="text-4xl font-bold mb-4 tracking-tight">
              Your AI workforce, on call.
            </h1>
            <p className="text-lg text-slate-400 max-w-md leading-relaxed">
              Map the AI workers your business needs, then put them to work — RoleForge
              turns your blueprint into a working team.
            </p>
            <div className="mt-8 space-y-3">
              <div className="flex items-center gap-3 text-sm text-slate-400">
                <div className="w-1.5 h-1.5 rounded-full bg-[#fe5963]"></div>
                A blueprint tailored to how you actually run the business
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-400">
                <div className="w-1.5 h-1.5 rounded-full bg-[#fe5963]"></div>
                Agents with clear roles, scopes, and handoffs
              </div>
              <div className="flex items-center gap-3 text-sm text-slate-400">
                <div className="w-1.5 h-1.5 rounded-full bg-[#fe5963]"></div>
                One workspace across blueprints, plans, and the team you build
              </div>
            </div>
          </div>
        </div>

        {/* Right side: login form */}
        <div className="flex w-full lg:w-1/2 items-center justify-center px-6">
          <div className="w-full max-w-sm">
            <div className="lg:hidden mb-10 flex flex-col items-center text-center">
              <img
                src="/roleforge-logo-horizontal.svg"
                alt="RoleForge"
                className="h-16 w-auto object-contain mb-2"
              />
              <p className="mt-2 text-sm text-slate-400">Your AI workforce, on call.</p>
            </div>

            <div className="mb-8">
              <h2 className="text-xl font-semibold">
                {fromPurchase ? 'Confirm your email' : 'Welcome back'}
              </h2>
              <p className="mt-1 text-sm text-slate-400">
                {fromPurchase
                  ? 'One more step on this device — send a sign-in link to open your Blueprint.'
                  : 'Sign in to pick up where your blueprint left off.'}
              </p>
            </div>

            {sent ? (
              <div className="rounded-lg border border-[#fe5963]/30 bg-[#fe5963]/10 px-4 py-4 text-sm">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-[#fe5963] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-white">Check your email</p>
                    <p className="mt-1 text-slate-300">
                      We sent a magic link to <span className="font-medium">{email}</span>. Click it
                      to finish signing in.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-1.5">
                    Email address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500 pointer-events-none" />
                    <input
                      type="email"
                      id="email"
                      required
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      className="w-full rounded-lg border border-white/10 bg-white/5 pl-10 pr-4 py-2.5 text-sm text-white placeholder-slate-500 focus:border-[#fe5963] focus:outline-none focus:ring-1 focus:ring-[#fe5963]/50 transition-colors"
                    />
                  </div>
                </div>

                {error && (
                  <div className="rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-2.5 text-sm text-red-400">
                    {error}
                  </div>
                )}

                {retryAfter > 0 && (
                  <div className="rounded-lg border border-[#fe5963]/30 bg-[#fe5963]/10 px-4 py-2.5 text-sm text-slate-300">
                    Link already sent — check your inbox, or retry in {retryAfter}s.
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting || retryAfter > 0}
                  className="w-full flex items-center justify-center gap-2 rounded-lg bg-[#fe5963] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#fe5963]/90 focus:outline-none focus:ring-2 focus:ring-[#fe5963]/50 focus:ring-offset-2 focus:ring-offset-[#080a0f] transition-all disabled:opacity-50"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Sending magic link...
                    </>
                  ) : retryAfter > 0 ? (
                    <>Retry in {retryAfter}s</>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      Send magic link
                    </>
                  )}
                </button>
              </form>
            )}

            {fromPurchase ? null : (
              <p className="mt-8 text-center text-sm text-slate-400">
                Don't have an account?{' '}
                <a
                  href="/#early-access"
                  className="text-[#fe5963] hover:text-[#fe5963]/80 transition-colors"
                >
                  Get Early Access
                </a>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
